"use client";

import React from "react";
import { Group } from "@visx/group";
import { BarRounded } from "@visx/shape";
import { scaleBand, scaleLinear } from "@visx/scale";
import { LinearGradient } from "@visx/gradient";
import { useTooltip, TooltipWithBounds, defaultStyles as tooltipStyles } from "@visx/tooltip";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { AxisBottom, AxisLeft } from "@visx/axis";
import institutionPageStyles from "../../../../uat/[institutionSlug]/styles.module.scss";
import { animated } from "react-spring";
import { BudgetEntry, PublicBudgetData } from "./types";
import { useTranslations } from "use-intl";
type BudgetDetailsDialogProps = {
  budget: PublicBudgetData | null;
  onOpenChange: (open: boolean) => void;
};
interface BudgetChartProps {
  entry: BudgetEntry;
}
interface QuarterData {
  quarter: string;
  amount: number;
}
const BudgetChart: React.FC<BudgetChartProps> = ({
  entry
}) => {
  const t = useTranslations();
  const data: QuarterData[] = [{
    quarter: t("budget.q1"),
    amount: entry.firstTrimester
  }, {
    quarter: t("budget.q2"),
    amount: entry.secondTrimester
  }, {
    quarter: t("budget.q3"),
    amount: entry.thirdTrimester
  }, {
    quarter: t("budget.q4"),
    amount: entry.fourthTrimester
  }];
  const width = 400;
  const height = 300;
  const margin = {
    top: 20,
    bottom: 20,
    left: 30,
    right: 20
  };
  const xScale = scaleBand({
    range: [margin.left, width - margin.right],
    domain: data.map(d => d.quarter),
    padding: 0.4
  });
  const maxAmount = Math.max(...data.map(d => d.amount), 0);
  const minAmount = Math.min(...data.map(d => d.amount), 0);
  const yScale = scaleLinear({
    range: [height - margin.bottom, margin.top],
    domain: [minAmount, maxAmount],
    nice: true
  });
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip
  } = useTooltip<QuarterData>();
  const axisColor = "#666";
  const filterID = "glow-effect";
  const AnimatedBarRounded = animated(BarRounded);
  return <div data-sentry-component="BudgetChart" data-sentry-source-file="BudgetDetailsDialog.tsx">
      <svg width={width} height={height + 20} data-sentry-element="svg" data-sentry-source-file="BudgetDetailsDialog.tsx">
        <defs data-sentry-element="defs" data-sentry-source-file="BudgetDetailsDialog.tsx">
          <filter id={filterID} x="-50%" y="-50%" width="200%" height="200%" data-sentry-element="filter" data-sentry-source-file="BudgetDetailsDialog.tsx">
            <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" data-sentry-element="feGaussianBlur" data-sentry-source-file="BudgetDetailsDialog.tsx" />
            <feMerge data-sentry-element="feMerge" data-sentry-source-file="BudgetDetailsDialog.tsx">
              <feMergeNode in="blur" data-sentry-element="feMergeNode" data-sentry-source-file="BudgetDetailsDialog.tsx" /> <feMergeNode in="SourceGraphic" data-sentry-element="feMergeNode" data-sentry-source-file="BudgetDetailsDialog.tsx" />
            </feMerge>
          </filter>
          <LinearGradient id="positive-bar-gradient" from="#53d616" to="#f082436f" data-sentry-element="LinearGradient" data-sentry-source-file="BudgetDetailsDialog.tsx" />
          <LinearGradient id="negative-bar-gradient" from="#f082436f" to="#e91e63" data-sentry-element="LinearGradient" data-sentry-source-file="BudgetDetailsDialog.tsx" />
        </defs>
        <Group data-sentry-element="Group" data-sentry-source-file="BudgetDetailsDialog.tsx">
          <AxisBottom scale={xScale} top={height - margin.bottom + 8} tickLength={0} tickStroke={axisColor} stroke="transparent" tickLabelProps={() => ({
          fill: axisColor,
          fontSize: 10,
          textAnchor: "middle"
        })} data-sentry-element="AxisBottom" data-sentry-source-file="BudgetDetailsDialog.tsx" />
          <AxisLeft scale={yScale} left={margin.left + 24} stroke={axisColor} tickStroke={axisColor} data-sentry-element="AxisLeft" data-sentry-source-file="BudgetDetailsDialog.tsx" />
          {data.map((d, i) => {
          const barHeight = Math.abs(yScale(d.amount) - yScale(0));
          const barY = d.amount > 0 ? yScale(d.amount) : yScale(0);
          const barFill = d.amount >= 0 ? "url(#positive-bar-gradient)" : "url(#negative-bar-gradient)";
          return <AnimatedBarRounded key={`bar-${i}`} x={xScale(d.quarter) ?? 0} y={barY} width={xScale.bandwidth()} height={barHeight} fill={barFill} radius={4} filter={`url(#${filterID})`} onMouseLeave={() => hideTooltip()} onMouseMove={event => {
            const svgRect = event.currentTarget.ownerSVGElement!.getBoundingClientRect();
            const tooltipX = event.clientX - svgRect.left + 50;
            const tooltipY = event.clientY - svgRect.top + 40;
            showTooltip({
              tooltipData: d,
              tooltipLeft: tooltipX,
              tooltipTop: tooltipY
            });
          }} />;
        })}
        </Group>
      </svg>
      {tooltipOpen && tooltipData &&
    // eslint-disable-next-line @shopify/jsx-no-hardcoded-content
    <TooltipWithBounds top={tooltipTop} left={tooltipLeft} style={tooltipStyles}>
          {`${tooltipData.quarter}: ${tooltipData.amount}`}
        </TooltipWithBounds>}
    </div>;
};
const BudgetDetailsDialog = ({
  budget,
  onOpenChange
}: BudgetDetailsDialogProps) => {
  const t = useTranslations();
  return <Dialog open={budget !== null} onOpenChange={onOpenChange} data-sentry-element="Dialog" data-sentry-component="BudgetDetailsDialog" data-sentry-source-file="BudgetDetailsDialog.tsx">
      {budget !== null && <DialogContent className="max-w-lg">
          <DialogTitle>
            {t("src_app_dashboard_accounting_budget_components_BudgetDetailsDialog_tsx_0")}
          </DialogTitle>
          <div className={`flex h-96 flex-col items-center gap-4 overflow-y-scroll ${institutionPageStyles["scrollable-contain er"]}`}>
            {budget.budgetEntries.map((entry, idx) => <div key={idx} className="flex flex-col items-center gap-4">
                {/* eslint-disable-next-line @shopify/jsx-no-hardcoded-content */}
                <h2 className="text-lg font-semibold text-glow">
                  {entry.code} - {entry.displayName}
                </h2>
                <BudgetChart key={entry.code} entry={entry} />
              </div>)}
          </div>
        </DialogContent>}
    </Dialog>;
};
export default BudgetDetailsDialog;